import { Loader2, Plus, Star, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { contractorService } from '@/services/contractor.service';
import type { Contractor } from '@/types/contractor';
import type { ListingContractor } from '@/types/listing';

interface ContractorSelectorProps {
  value: ListingContractor[];
  onChange: (contractors: ListingContractor[]) => void;
  label?: string;
  maxContractors?: number;
  className?: string;
}

export function ContractorSelector({
  value = [],
  onChange,
  label = 'Contractors',
  maxContractors = 10,
  className = '',
}: ContractorSelectorProps) {
  const [availableContractors, setAvailableContractors] = useState<Contractor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedContractorId, setSelectedContractorId] = useState<string>('');
  const [estimate, setEstimate] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const fetchContractors = useCallback(async () => {
    try {
      setIsLoading(true);
      const contractors = await contractorService.getActive();
      setAvailableContractors(contractors);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load contractors');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContractors();
  }, [fetchContractors]);

  const handleAddContractor = useCallback(() => {
    if (!selectedContractorId) return;

    const contractor = availableContractors.find((c) => c._id === selectedContractorId);
    if (!contractor) return;

    const newContractor: ListingContractor = {
      contractorId: selectedContractorId,
      renovationEstimate: estimate ? parseFloat(estimate) : 0,
      notes: notes || undefined,
      contractor: {
        _id: contractor._id,
        name: contractor.name,
        companyName: contractor.companyName,
        profileLogo: contractor.profileLogo,
        rating: contractor.rating,
      },
    };

    onChange([...value, newContractor]);
    setShowAddDialog(false);
    setSelectedContractorId('');
    setEstimate('');
    setNotes('');
  }, [selectedContractorId, estimate, notes, availableContractors, value, onChange]);

  const handleRemoveContractor = useCallback(
    (index: number) => {
      const newValue = [...value];
      newValue.splice(index, 1);
      onChange(newValue);
    },
    [value, onChange]
  );

  const handleUpdateEstimate = useCallback(
    (index: number, newEstimate: number) => {
      const newValue = [...value];
      const existing = newValue[index];
      if (existing) {
        const updated: ListingContractor = {
          contractorId: existing.contractorId,
          renovationEstimate: newEstimate,
          notes: existing.notes,
          contractor: existing.contractor,
        };
        newValue[index] = updated;
        onChange(newValue);
      }
    },
    [value, onChange]
  );

  const handleUpdateNotes = useCallback(
    (index: number, newNotes: string) => {
      const newValue = [...value];
      const existing = newValue[index];
      if (existing) {
        const updated: ListingContractor = {
          contractorId: existing.contractorId,
          renovationEstimate: existing.renovationEstimate,
          notes: newNotes || undefined,
          contractor: existing.contractor,
        };
        newValue[index] = updated;
        onChange(newValue);
      }
    },
    [value, onChange]
  );

  // Filter out already selected contractors
  const unselectedContractors = availableContractors.filter(
    (c) => !value.some((v) => v.contractorId === c._id)
  );

  const canAddMore = value.length < maxContractors && unselectedContractors.length > 0;

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-[2px]">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={12}
            className={star <= rating ? 'fill-[#f59e0b] text-[#f59e0b]' : 'text-[#e5e5e5]'}
          />
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center py-[24px] ${className}`}>
        <Loader2 size={24} className="animate-spin text-[#055135]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-[8px] bg-[#fee] p-[16px] text-[14px] text-[#d4183d] ${className}`}>
        {error}
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="mb-[12px] flex items-center justify-between">
        <span className="text-[14px] font-medium text-[#181818]">
          {label}
          <span className="ml-[8px] text-[12px] font-normal text-[#8d8c8f]">
            ({value.length}/{maxContractors})
          </span>
        </span>
        {canAddMore && (
          <button
            type="button"
            onClick={() => setShowAddDialog(true)}
            className="flex items-center gap-[4px] rounded-[6px] bg-[#055135] px-[12px] py-[6px] text-[12px] font-medium text-white transition-colors hover:bg-[#044128]"
          >
            <Plus size={14} />
            Add Contractor
          </button>
        )}
      </div>

      {value.length === 0 ? (
        <div className="rounded-[8px] border-2 border-dashed border-[#e5e5e5] p-[24px] text-center">
          <p className="text-[14px] text-[#8d8c8f]">No contractors assigned yet</p>
          {canAddMore && (
            <button
              type="button"
              onClick={() => setShowAddDialog(true)}
              className="mt-[12px] inline-flex items-center gap-[4px] text-[14px] font-medium text-[#055135] hover:underline"
            >
              <Plus size={16} />
              Add your first contractor
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-[12px]">
          {value.map((item, index) => (
            <div
              key={item.contractorId}
              className="rounded-[12px] border border-[#e5e5e5] bg-white p-[16px]"
            >
              <div className="flex items-start gap-[12px]">
                {item.contractor?.profileLogo ? (
                  <img
                    src={item.contractor.profileLogo}
                    alt={item.contractor.name}
                    className="h-[48px] w-[48px] rounded-full border border-[#e5e5e5] object-cover"
                  />
                ) : (
                  <div className="flex h-[48px] w-[48px] items-center justify-center rounded-full bg-[#055135] text-[16px] font-semibold text-white">
                    {item.contractor?.name?.charAt(0) || '?'}
                  </div>
                )}

                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[14px] font-semibold text-[#181818]">
                        {item.contractor?.name || 'Unknown'}
                      </p>
                      <p className="text-[12px] text-[#8d8c8f]">{item.contractor?.companyName}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveContractor(index)}
                      className="flex h-[28px] w-[28px] items-center justify-center rounded-[6px] text-[#646464] transition-colors hover:bg-[#fee] hover:text-[#d4183d]"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {item.contractor?.rating !== undefined && (
                    <div className="mt-[4px]">{renderStars(item.contractor.rating)}</div>
                  )}

                  <div className="mt-[12px] grid grid-cols-2 gap-[12px]">
                    <label className="block">
                      <span className="mb-[4px] block text-[12px] font-medium text-[#181818]">
                        Renovation Estimate ($)
                        <span className="ml-[4px] font-normal text-[#8d8c8f]">(Internal)</span>
                      </span>
                      <input
                        type="number"
                        value={item.renovationEstimate || ''}
                        onChange={(e) =>
                          handleUpdateEstimate(index, parseFloat(e.target.value) || 0)
                        }
                        className="h-[36px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[10px] text-[13px] focus:border-[#055135] focus:outline-none"
                        placeholder="Enter estimate"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-[4px] block text-[12px] font-medium text-[#181818]">
                        Notes
                        <span className="ml-[4px] font-normal text-[#8d8c8f]">(Internal)</span>
                      </span>
                      <input
                        type="text"
                        value={item.notes || ''}
                        onChange={(e) => handleUpdateNotes(index, e.target.value)}
                        className="h-[36px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[10px] text-[13px] focus:border-[#055135] focus:outline-none"
                        placeholder="Optional notes"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Contractor Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-[480px] rounded-[16px] bg-white p-[24px] shadow-xl">
            <h3 className="mb-[16px] text-[18px] font-semibold text-[#181818]">Add Contractor</h3>

            <div className="space-y-[16px]">
              <label className="block">
                <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
                  Select Contractor
                </span>
                <select
                  value={selectedContractorId}
                  onChange={(e) => setSelectedContractorId(e.target.value)}
                  className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] focus:border-[#055135] focus:outline-none"
                >
                  <option value="">Choose a contractor...</option>
                  {unselectedContractors.map((contractor) => (
                    <option key={contractor._id} value={contractor._id}>
                      {contractor.name} - {contractor.companyName}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">
                  Renovation Estimate ($)
                </span>
                <input
                  type="number"
                  value={estimate}
                  onChange={(e) => setEstimate(e.target.value)}
                  className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] focus:border-[#055135] focus:outline-none"
                  placeholder="e.g., 75000"
                />
                <span className="mt-[4px] block text-[11px] text-[#8d8c8f]">
                  Internal only – not shown to end users
                </span>
              </label>

              <label className="block">
                <span className="mb-[6px] block text-[13px] font-medium text-[#181818]">Notes</span>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="h-[40px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[12px] text-[13px] focus:border-[#055135] focus:outline-none"
                  placeholder="Additional notes..."
                />
                <span className="mt-[4px] block text-[11px] text-[#8d8c8f]">
                  Internal only – not shown to end users
                </span>
              </label>
            </div>

            <div className="mt-[24px] flex justify-end gap-[12px]">
              <button
                type="button"
                onClick={() => {
                  setShowAddDialog(false);
                  setSelectedContractorId('');
                  setEstimate('');
                  setNotes('');
                }}
                className="rounded-[6px] border border-[#e5e5e5] px-[16px] py-[8px] text-[13px] font-medium text-[#646464] transition-colors hover:bg-[#f5f5f5]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddContractor}
                disabled={!selectedContractorId}
                className="rounded-[6px] bg-[#055135] px-[16px] py-[8px] text-[13px] font-medium text-white transition-colors hover:bg-[#044128] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Add Contractor
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
